/**
 * Distance-along-the-route maths, so the car can be drawn ON the road rather
 * than on a straight line between two fixes.
 *
 * Everything works in metres on a local flat projection. At city scale the
 * error against a proper geodesic is centimetres, and the alternative is
 * trigonometry per segment per animation frame.
 */

const METERS_PER_DEG_LAT = 111320;
const toRad = (deg) => (deg * Math.PI) / 180;

/**
 * Pre-computes what the per-frame maths needs: the path in metres, the
 * cumulative distance to each point, and the total.
 *
 * @param  {{lat: number, lng: number}[]} points - The decoded route
 * @returns {object|null} - Geometry, or null for a path with nothing to ride
 */
export const buildPath = (points) => {
  if (!points || points.length < 2) {
    return null;
  }

  const originLat = points[0].lat;
  const metersPerDegLng = METERS_PER_DEG_LAT * Math.cos(toRad(originLat));
  const xy = points.map((p) => ({ x: p.lng * metersPerDegLng, y: p.lat * METERS_PER_DEG_LAT }));

  const cumulative = [0];
  for (let i = 1; i < xy.length; i += 1) {
    const dx = xy[i].x - xy[i - 1].x;
    const dy = xy[i].y - xy[i - 1].y;
    cumulative.push(cumulative[i - 1] + Math.sqrt(dx * dx + dy * dy));
  }

  return { points, xy, cumulative, total: cumulative[cumulative.length - 1], metersPerDegLng };
};

/**
 * Where along the path a point sits, and how far off it is.
 *
 * The offset matters: a driver who has genuinely left the route should be
 * drawn where they are, not dragged back onto a road they are not on.
 *
 * @param  {object} path - From {@link buildPath}
 * @param  {{lat: number, lng: number}} point - The reported position
 * @returns {{along: number, offsetMeters: number}} - Distance along and away
 */
export const projectToPath = (path, point) => {
  const px = point.lng * path.metersPerDegLng;
  const py = point.lat * METERS_PER_DEG_LAT;

  let best = { along: 0, offsetMeters: Infinity };

  for (let i = 1; i < path.xy.length; i += 1) {
    const a = path.xy[i - 1];
    const b = path.xy[i];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSq = dx * dx + dy * dy;
    // A repeated point: no direction to project onto, so the segment is skipped
    // rather than dividing by zero.
    if (lengthSq === 0) {
      continue; // eslint-disable-line no-continue
    }
    const t = Math.max(0, Math.min(1, ((px - a.x) * dx + (py - a.y) * dy) / lengthSq));
    const cx = a.x + t * dx;
    const cy = a.y + t * dy;
    const offsetMeters = Math.hypot(px - cx, py - cy);
    if (offsetMeters < best.offsetMeters) {
      best = { along: path.cumulative[i - 1] + t * Math.sqrt(lengthSq), offsetMeters };
    }
  }

  return best;
};

/** Compass bearing from one point to the next, degrees clockwise from north. */
export const bearingBetween = (from, to) => {
  const dLng = toRad(to.lng - from.lng);
  const y = Math.sin(dLng) * Math.cos(toRad(to.lat));
  const x =
    Math.cos(toRad(from.lat)) * Math.sin(toRad(to.lat)) -
    Math.sin(toRad(from.lat)) * Math.cos(toRad(to.lat)) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
};

/**
 * Index of the first point at least `along` metres in (`strict`: more than),
 * by binary search on the cumulative distances — this runs every animation
 * frame, and a long route is thousands of points.
 */
export const firstIndexAtOrPast = (path, along, strict = false) => {
  let lo = 0;
  let hi = path.cumulative.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    const past = strict ? path.cumulative[mid] > along : path.cumulative[mid] >= along;
    if (past) hi = mid;
    else lo = mid + 1;
  }
  return lo;
};

/**
 * The position and heading at a distance along the path. Clamped at both ends.
 *
 * Heading comes from the segment rather than from the device, so the car is
 * always square to the road it is on.
 *
 * @param  {object} path - From {@link buildPath}
 * @param  {number} along - Metres from the start
 * @returns {{lat: number, lng: number, bearing: number}} - Where to draw
 */
export const pointAtDistance = (path, along) => {
  const clamped = Math.max(0, Math.min(path.total, along));

  const index = Math.min(path.cumulative.length - 1, Math.max(1, firstIndexAtOrPast(path, clamped)));

  const from = path.points[index - 1];
  const to = path.points[index];
  const segmentStart = path.cumulative[index - 1];
  const segmentLength = path.cumulative[index] - segmentStart;
  const t = segmentLength === 0 ? 0 : (clamped - segmentStart) / segmentLength;

  return {
    lat: from.lat + (to.lat - from.lat) * t,
    lng: from.lng + (to.lng - from.lng) * t,
    bearing: bearingBetween(from, to),
  };
};

/**
 * The part of the route still ahead: the point at `along`, then every point
 * after it.
 *
 * Drawing the whole route behind a car that has already driven it is the
 * navigation equivalent of leaving the breadcrumbs in — the line ops cares
 * about is the one the car has not covered yet.
 *
 * @param  {object} path - From {@link buildPath}
 * @param  {number} along - Metres the car has covered
 * @returns {{lat: number, lng: number}[]} - The remaining route
 */
export const pathAhead = (path, along) => {
  if (!path) {
    return [];
  }
  if (along === undefined || along === null) {
    return path.points;
  }

  const clamped = Math.max(0, Math.min(path.total, along));
  const head = pointAtDistance(path, clamped);
  const rest = path.points.slice(firstIndexAtOrPast(path, clamped, true));

  return [{ lat: head.lat, lng: head.lng }, ...rest];
};
